<template>
  <div
    class="call-me-view"
    data-component="CallMeView"
  >
    <CardInfoComponent
      icon="icon-life"
      text-align="start"
    >
      {{ t('copy_life_call-me-view_hint') }}</CardInfoComponent
    >
    <div class="info">
      <p class="caption">{{ t('components.ProductCardComponent.caption') }}</p>
      <h2 class="title">{{ product.title }}</h2>
      <h3 class="price">
        {{
          t('copy_life_call-me-view_price', {
            price: `${priceFormat(answers.price)}${product.units}`,
            period: billingCycleText
          })
        }}
      </h3>
      <p class="text">
        {{ t('copy_life_call-me-view_text') }}
      </p>
      <div class="request-number">
        {{ product.quotationNumber }}
      </div>
    </div>
    <hr />
    <div class="call-me">
      <template v-if="isMobile">
        <BaseButton
          atphone
          type="outlined"
          @click="onPhoneNumberClick"
          class="atphone"
        >
          {{ t('copy_button_emission_contact') }}
        </BaseButton>
      </template>
      <template v-else>
        <div>
          <p class="phone-title">{{ t('copy_life_call-me-view_phone-title') }}</p>
          <span class="phone-number">{{ phoneNumber }}</span>
        </div>
      </template>
      <div>
        <p class="text">{{ t('copy_life_call-me-view_phone-text') }}</p>
        <BaseButton
          type="transparent"
          @click="openCallMeModal"
        >
          {{ t('copy_life_call-me-view_phone-button') }}
        </BaseButton>
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
  import { computed } from 'vue'
  import { useI18n } from 'vue-i18n'

  import CardInfoComponent from '@/modules/common/common/components/panels/CardInfoComponent.vue'
  import { BaseButton } from '@/modules/core/components'
  import { useIsMobile } from '@/modules/core/composables/useIsMobile'
  import { useCommonStore } from '@/modules/core/stores/commonStore'
  import { useSupportModalStore } from '@/modules/core/stores/supportModalStore'
  import { priceFormat } from '@/utils/textUtils'
  import type LifeConfigurations from '../../common/models/APP/LifeConfigurations'
  import type LifeHiringAnswers from '../models/APP/LifeHiringAnswers'

  const { t } = useI18n()
  const commonStore = useCommonStore()
  const supportModalStore = useSupportModalStore()
  const { isMobile } = useIsMobile()

  interface PropsInterface {
    answers: LifeHiringAnswers
    configurations: LifeConfigurations
  }

  const props = withDefaults(defineProps<PropsInterface>(), {})
  const answers = props.answers
  const product = answers.product!
  const billingCycleText = computed(() => {
    const billingCycle = answers.billingCycle!
    return (props.configurations.billingCycles as Record<string, { textInPrice: string }>)[
      billingCycle
    ].textInPrice
  })
  const phoneNumber = computed(
    () => commonStore.getContactPhone() || t('components.support-chat-call.free-phone-number')
  )
  function onPhoneNumberClick() {
    window.location.href = `tel:${phoneNumber.value.replace(/\s/g, '')}`
  }
  function openCallMeModal() {
    supportModalStore.setOpenModal(true)
  }
</script>

<style lang="scss" scoped>
  .call-me-view {
    text-align: center;

    .caption {
      color: $color-neutral;
      font-size: $font-size-xs;
      opacity: 1;
      transition: all 0.3s ease-in-out;

      @include breakpoint-lg {
        font-size: $font-size-s;
      }
    }

    .title {
      font-family: $font-family-secondary;
      font-size: $font-size-2xl;
      font-weight: $font-weight-500;
      margin-bottom: $spacing-2xl;
      transition: all 0.3s ease-in-out;

      @include breakpoint-lg {
        margin-bottom: 0;
      }
    }

    .price {
      font-size: $font-size-2xl;
      color: $color-primary-dark;
      transition: all 0.3s ease-in-out;

      @include breakpoint-lg {
        margin-bottom: $spacing-l;
      }
    }

    .text {
      margin: $spacing-l;
      font-size: $font-size-l;
      font-weight: $font-weight-400;
      line-height: $line-height-140p;
    }

    .phone-title {
      color: $color-primary-dark;
      font-size: $font-size-20;
      font-weight: $font-weight-500;
      line-height: 135%;
      margin-bottom: $spacing-s;
    }

    .phone-number {
      font-size: $font-size-xl;
      font-style: bold;
      font-weight: $font-weight-700;
      line-height: $line-height-130p;
    }

    .request-number {
      margin: 0 auto;
      width: fit-content;
      background-color: $color-blue-200;
      color: $color-neutral-dark;
      padding: $spacing-s $spacing-xl;
      font-weight: $font-weight-700;
      border-radius: $b-radius-50;
    }
  }
</style>
